import { jsx } from 'react/jsx-runtime';
import { ProgressBar } from '../../atoms/ProgressBar/ProgressBar.js';

/**
 * ProgressBars
 *
 * A presentational component that renders a sequence of progress indicators (steps/bars)
 * to represent progress through a multi-step process.
 *
 * The component is controlled via two numeric props:
 *
 * @param props.total - The total number of steps/segments to render. Must be an integer >= 1.
 *   When 1 is supplied, the component renders a single filled bar. The component does not
 *   render when `total` is less than 1.
 *
 * @param props.current - The currently active step (1-based index). Values outside the valid
 *   range will be clamped to the inclusive range [1, total]. The actively selected step is
 *   visually distinguished to indicate progress.
 *
 * Behavior and accessibility notes:
 * - The component is intended to be purely presentational. It does not manage step state;
 *   callers should control `current` and `total`.
 * - Treat `current` as a 1-based index (first step = 1).
 * - The implementation should expose appropriate accessibility attributes (for example,
 *   `aria-current="step"` or a `role="progressbar"` with `aria-valuemin`, `aria-valuemax`
 *   and `aria-valuenow`) so assistive technologies can convey progress information.
 * - Visual styling (orientation, colors, spacing) is implementation-defined; consumers
 *   should rely on props for state and not inspect DOM internals.
 *
 * Example:
 * const [step, setStep] = useState(1);
 * <ProgressBars total={5} current={step} />
 *
 * Edge cases:
 * - If `total` is updated to a value smaller than `current`, the caller should update `current`
 *   accordingly; well-behaved implementations will render with `current` clamped to `total`.
 *
 * Returns:
 * - A JSX.Element representing the progress bars/steps.
 *
 * Adapted from the previous implementation here as part of /b2bce-repair-and-care
 * https://github.com/domgen/b2bce-repair-and-care-frontend/blob/6543a0be504762b651fb13fdcad248ea3cc91c2e/src/components/molecules/ProgressBars.tsx#L6
 */
const ProgressBars = ({ total, current, ref = null, }) => {
    return (current <= total && (jsx("div", { ref: ref, className: "flex h-2 gap-2 w-auto", children: Array.from({ length: total }).map((_, index) => {
            return (jsx(ProgressBar, { achieved: index < current, isCurrent: index === current - 1 }, index));
        }) })));
};

export { ProgressBars };
